// src/utils/excelTemplateGenerator.js
import ExcelJS from 'exceljs';
import { getCities } from '../services/api/orderReference';
import { searchProducts } from '../services/api/productSearch';
import { getProductCategories } from '../services/api/categories';
import { getBrands } from '../services/api/brands';

const REQUIRED_FIELDS_COLOR = 'FFFEF08A'; // Light yellow
const HEADER_COLOR = 'FF667EEA'; // Purple
const INVALID_BORDER_COLOR = 'FFEF4444'; // Red
const INVALID_FIELDS_COLOR = 'FFC5C5'; // Light yellow

export const generateBulkOrderTemplate = async (shopId, options = {}) => {
  const { selectedFields = {}, numberOfItems = 3 } = options;

  // Fetch reference data
  const citiesResponse = await getCities({ includeLocation: true });
  const cities = citiesResponse?.cities || citiesResponse || [];
  
  const productsResponse = await searchProducts(shopId, '', { limit: 1000, page: 1 });
  const products = productsResponse?.products || [];

  // Create workbook
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Bulk Order System';
  workbook.created = new Date();

  // Create sheets
  const ordersSheet = workbook.addWorksheet('Orders', {
    views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }]
  });
  const citiesSheet = workbook.addWorksheet('Cities Reference');
  const productsSheet = workbook.addWorksheet('Products Reference');
  const paymentTypesSheet = workbook.addWorksheet('Payment Types');
  const orderStatusesSheet = workbook.addWorksheet('Order Statuses');

  // Define columns
  const columns = buildColumns(selectedFields, numberOfItems);
  ordersSheet.columns = columns;

  // Style headers
  styleHeaders(ordersSheet, columns, selectedFields, numberOfItems);

  // Add sample data
  addSampleData(ordersSheet, columns, cities, products);

  // Populate reference sheets
  populateCitiesReference(citiesSheet, cities);
  populateProductsReference(productsSheet, products);
  populatePaymentTypes(paymentTypesSheet);
  populateOrderStatuses(orderStatusesSheet);

  // Apply data validation
  applyDataValidation(ordersSheet, columns, workbook, selectedFields, numberOfItems);

  // Apply conditional formatting
  applyConditionalFormatting(ordersSheet, columns, selectedFields, numberOfItems);

  // Hide reference sheets
  // citiesSheet.state = 'hidden';
  // productsSheet.state = 'hidden';
  paymentTypesSheet.state = 'hidden';
  orderStatusesSheet.state = 'hidden';

  return workbook;
};

export const generateBulkProductTemplate = async (warehouseName) => {
  const [categoriesResponse, brandsResponse] = await Promise.all([
    getProductCategories(),
    getBrands(),
  ]);
  const categories = categoriesResponse?.categories || [];
  const brands = brandsResponse?.brands || [];

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Bulk Product Import';
  workbook.created = new Date();

  const productsSheet = workbook.addWorksheet('Products', {
    views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }]
  });
  const categoriesSheet = workbook.addWorksheet('Categories Reference');
  const brandsSheet = workbook.addWorksheet('Brands Reference');

  const columns = buildProductColumns(warehouseName);
  productsSheet.columns = columns;

  styleProductHeaders(productsSheet, columns);
  addProductSampleData(productsSheet, columns, categories, brands);

  populateNameReference(categoriesSheet, categories, 'Category Name');
  populateNameReference(brandsSheet, brands, 'Brand Name');

  applyProductDataValidation(productsSheet, columns);
  applyProductConditionalFormatting(productsSheet, columns);

  return workbook;
};

function buildProductColumns(warehouseName) {
  return [
    {
      header: 'SKU',
      key: 'sku',
      width: 20,
      required: true,
      note: 'Required. Unique SKU. An existing SKU updates that product; a new SKU creates one.\nExample: PROD-001'
    },
    {
      header: 'Name',
      key: 'name',
      width: 30,
      required: true,
      note: 'Required. Product name.\nExample: Cotton T-Shirt'
    },
    {
      header: 'Printable_Name',
      key: 'printableName',
      width: 30,
      note: 'Optional. Name printed on waybill labels instead of the product name, when set.\nExample: Cotton Tee'
    },
    {
      header: 'Description',
      key: 'description',
      width: 35,
      note: 'Optional. Product description.'
    },
    {
      header: 'Cost_Price',
      key: 'costPrice',
      width: 15,
      note: 'Optional. Your buying/cost price.\nExample: 500'
    },
    {
      header: 'Barcode',
      key: 'barcode',
      width: 20,
      note: 'Optional. Product barcode.'
    },
    {
      header: 'Weight',
      key: 'weight',
      width: 12,
      note: 'Optional. Weight in kg.\nExample: 0.5'
    },
    {
      header: 'Category',
      key: 'category',
      width: 20,
      note: 'Optional. Category name (see Categories Reference). Unrecognised names are created automatically.\nExample: Apparel'
    },
    {
      header: 'Brand',
      key: 'brand',
      width: 20,
      note: 'Optional. Brand name (see Brands Reference). Unrecognised names are created automatically.\nExample: Acme'
    },
    {
      header: 'Price',
      key: 'price',
      width: 15,
      required: true,
      note: 'Required. Selling price applied to every shop selected for this import.\nExample: 1500'
    },
    {
      header: 'Opening_Stock',
      key: 'openingStock',
      width: 18,
      note: `Optional. Initial stock quantity${warehouseName ? ` for warehouse "${warehouseName}"` : ''}.\nExample: 100`
    },
    {
      header: 'Visibility',
      key: 'visibility',
      width: 15,
      note: 'Optional. Select from dropdown. Defaults to "public" if left blank.\nOptions: public, draft, trash'
    },
  ];
}

function styleProductHeaders(sheet, columns) {
  const headerRow = sheet.getRow(1);

  columns.forEach((col, index) => {
    const cell = headerRow.getCell(index + 1);

    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: HEADER_COLOR }
    };
    cell.font = {
      bold: true,
      color: { argb: 'FFFFFFFF' },
      size: 11
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'left',
      wrapText: true
    };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      right: { style: 'thin', color: { argb: 'FFD1D5DB' } }
    };

    if (col.note) {
      cell.note = {
        texts: [{ text: col.note }],
        margins: {
          insetmode: 'custom',
          inset: [0.13, 0.13, 0.13, 0.13]
        }
      };
    }
  });

  headerRow.height = 25;
}

function addProductSampleData(sheet, columns, categories, brands) {
  const sampleCategory = categories.length > 0 ? categories[0].name : 'Apparel';
  const sampleBrand = brands.length > 0 ? brands[0].name : '';

  const sampleRow = {
    sku: 'PROD-001',
    name: 'Cotton T-Shirt',
    printableName: '',
    description: 'A soft, breathable cotton t-shirt',
    costPrice: 500,
    barcode: '',
    weight: 0.2,
    category: sampleCategory,
    brand: sampleBrand,
    price: 1500,
    openingStock: 100,
    visibility: 'public',
  };

  const row = sheet.addRow(sampleRow);

  row.eachCell((cell, colNumber) => {
    const column = columns[colNumber - 1];

    if (column && column.required) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: REQUIRED_FIELDS_COLOR }
      };
    }

    cell.border = {
      top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      right: { style: 'thin', color: { argb: 'FFE5E7EB' } }
    };
  });
}

function populateNameReference(sheet, items, headerLabel) {
  sheet.columns = [{ header: headerLabel, key: 'name', width: 30 }];

  items.forEach(item => {
    sheet.addRow({ name: item.name });
  });

  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFF3F4F6' }
  };
}

function applyProductDataValidation(sheet, columns) {
  const maxRows = 1000;

  columns.forEach((col, index) => {
    const colLetter = String.fromCharCode(65 + index);

    if (col.key === 'price' || col.key === 'costPrice') {
      sheet.dataValidations.add(`${colLetter}2:${colLetter}${maxRows}`, {
        type: 'decimal',
        operator: 'greaterThanOrEqual',
        showErrorMessage: true,
        allowBlank: col.key !== 'price',
        formulae: [0],
        errorStyle: col.key === 'price' ? 'error' : 'warning',
        errorTitle: 'Invalid Amount',
        error: 'Amount must be a positive number or zero.'
      });
    }

    if (col.key === 'openingStock' || col.key === 'weight') {
      sheet.dataValidations.add(`${colLetter}2:${colLetter}${maxRows}`, {
        type: 'decimal',
        operator: 'greaterThanOrEqual',
        showErrorMessage: true,
        allowBlank: true,
        formulae: [0],
        errorStyle: 'warning',
        errorTitle: 'Invalid Value',
        error: 'Value must be a positive number or zero.'
      });
    }

    if (col.key === 'visibility') {
      sheet.dataValidations.add(`${colLetter}2:${colLetter}${maxRows}`, {
        type: 'list',
        allowBlank: true,
        formulae: ['"public,draft,trash"'],
        showErrorMessage: true,
        errorStyle: 'error',
        errorTitle: 'Invalid Visibility',
        error: 'Please select a valid visibility from the dropdown.'
      });
    }
  });
}

function applyProductConditionalFormatting(sheet, columns) {
  const maxRows = 1000;

  columns.forEach((col, index) => {
    const colLetter = String.fromCharCode(65 + index);

    if (col.required) {
      sheet.addConditionalFormatting({
        ref: `${colLetter}2:${colLetter}${maxRows}`,
        rules: [
          {
            type: 'expression',
            formulae: ['TRUE'],
            style: {
              fill: {
                type: 'pattern',
                pattern: 'solid',
                bgColor: { argb: REQUIRED_FIELDS_COLOR }
              }
            }
          }
        ]
      });
    }
  });
}

function buildColumns(selectedFields, numberOfItems) {
  const columns = [
    {
      header: 'Customer_Name',
      key: 'customerName',
      width: 20,
      required: true,
      note: 'Required. Full name of the customer.\nExample: John Doe'
    },
    {
      header: 'Address',
      key: 'address',
      width: 30,
      required: true,
      note: 'Required. Complete delivery address.\nExample: 123 Main St, Apartment 4B'
    },
    {
      header: 'Phone_1',
      key: 'phone1',
      width: 15,
      required: true,
      note: 'Required. Primary contact number (10-15 digits).\nExample: 0771234567'
    },
    {
      header: 'Phone_2',
      key: 'phone2',
      width: 15,
      note: 'Optional. Secondary contact number.\nExample: 0112345678'
    },
    {
      header: 'City_Name',
      key: 'cityName',
      width: 20,
      required: true,
      note: 'Required. City name (must match Cities Reference).\nExample: Colombo 01'
    }
  ];

  // Optional fields
  if (selectedFields.customerEmail) {
    columns.push({
      header: 'Customer_Email',
      key: 'customerEmail',
      width: 25,
      note: 'Optional. Customer email address.\nExample: john@example.com'
    });
  }

  if (selectedFields.privateReference) {
    columns.push({
      header: 'Private_Reference',
      key: 'privateReference',
      width: 20,
      note: 'Optional. Internal reference code.\nExample: REF-001'
    });
  }
  
  if (selectedFields.paymentType) {
    columns.push({
      header: 'Payment_Type',
      key: 'paymentType',
      width: 18,
      note: 'Required. Select from dropdown.\nOptions: cash_on_delivery, fully_paid'
    });
  }

  if (selectedFields.orderStatus) {
    columns.push({
      header: 'Order_Status',
      key: 'orderStatus',
      width: 15,
      note: 'Required. Select from dropdown.\nOptions: pending, confirmed'
    });
  }

  if (selectedFields.deliveryNotes) {
    columns.push({
      header: 'Delivery_Notes',
      key: 'deliveryNotes',
      width: 30,
      note: 'Optional. Special delivery instructions.\nExample: Call before delivery'
    });
  }

  if (selectedFields.specialNotes) {
    columns.push({
      header: 'Special_Notes',
      key: 'specialNotes',
      width: 30,
      note: 'Optional. Additional order notes.\nExample: Handle with care'
    });
  }

  // Item columns
  for (let i = 1; i <= numberOfItems; i++) {
    columns.push({
      header: `Item_${i}_SKU`,
      key: `item${i}Sku`,
      width: 20,
      required: i === 1,
      note: `${i === 1 ? 'Required' : 'Optional'}. Product SKU (must match Products Reference).\nExample: PROD-001`
    });
    columns.push({
      header: `Item_${i}_Quantity`,
      key: `item${i}Quantity`,
      width: 15,
      required: i === 1,
      note: `${i === 1 ? 'Required' : 'Optional'}. Quantity (positive number > 0).\nExample: 2`
    });
    if (selectedFields.customLineTotal) {
      columns.push({
        header: `Item_${i}_Custom_Line_Total`,
        key: `item${i}CustomLineTotal`,
        width: 20,
        note: 'Optional. Override auto-calculated price.\nExample: 1500.00'
      });
    }
  }

  if (selectedFields.deliveryCharge) {
    columns.push({
      header: 'Delivery Charge',
      key: 'deliveryCharge',
      width: 15,
      note: 'Optional. Delivery fee amount.\nExample: 300'
    });
  }

  if (selectedFields.totalDiscount) {
    columns.push({
      header: 'Total Discount',
      key: 'totalDiscount',
      width: 15,
      note: 'Optional. Total discount amount.\nExample: 100'
    });
  }

  return columns;
}

function styleHeaders(ordersSheet, columns, selectedFields, numberOfItems) {
  const headerRow = ordersSheet.getRow(1);
  
  columns.forEach((col, index) => {
    const cell = headerRow.getCell(index + 1);
    
    // Base header style
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: HEADER_COLOR }
    };
    cell.font = {
      bold: true,
      color: { argb: 'FFFFFFFF' },
      size: 11
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'left',
      wrapText: true
    };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      right: { style: 'thin', color: { argb: 'FFD1D5DB' } }
    };

    // Add comment/note
    if (col.note) {
      cell.note = {
        texts: [{ text: col.note }],
        margins: {
          insetmode: 'custom',
          inset: [0.13, 0.13, 0.13, 0.13]
        }
      };
    }
  });

  headerRow.height = 25;
}

function addSampleData(ordersSheet, columns, cities, products) {
  const sampleCity = cities.length > 0 ? cities[0].name : 'Colombo 01';
  const sampleProduct = products.length > 0 ? products[0].sku : 'PROD-001';

  const sampleRow = {
    customerName: 'John Doe',
    customerEmail: 'john@example.com',
    address: '123 Main St, Apartment 4B',
    phone1: '0771234567',
    phone2: '0112345678',
    cityName: sampleCity,
    paymentType: 'cash_on_delivery',
    orderStatus: 'pending',
    privateReference: 'REF-001',
    deliveryNotes: 'Call before delivery',
    specialNotes: 'Handle with care',
    item1Sku: sampleProduct,
    item1Quantity: 2,
    item1CustomLineTotal: '',
    item2Sku: products.length > 1 ? products[1].sku : '',
    item2Quantity: 1,
    item2CustomLineTotal: '',
    deliveryCharge: 300,
    totalDiscount: 100
  };

  const row = ordersSheet.addRow(sampleRow);
  
  // Style sample row
  row.eachCell((cell, colNumber) => {
    const column = columns[colNumber - 1];
    
    if (column && column.required) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: REQUIRED_FIELDS_COLOR }
      };
    }
    
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      right: { style: 'thin', color: { argb: 'FFE5E7EB' } }
    };
  });
}

function populateCitiesReference(sheet, cities) {
  sheet.columns = [
    { header: 'City Name', key: 'name', width: 30 },
    { header: 'District', key: 'district', width: 20 },
    { header: 'Province', key: 'province', width: 20 }
  ];

  cities.forEach(city => {
    sheet.addRow({
      name: city.name,
      district: city.District?.name || '',
      province: city.District?.Province?.name || ''
    });
  });

  // Style header
  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFF3F4F6' }
  };
}

function populateProductsReference(sheet, products) {
  sheet.columns = [
    { header: 'SKU', key: 'sku', width: 20 },
    { header: 'Product Name', key: 'name', width: 40 },
    { header: 'Price', key: 'price', width: 15 },
    { header: 'Stock', key: 'stock', width: 12 }
  ];

  products.forEach(product => {
    sheet.addRow({
      sku: product.sku,
      name: product.displayName + (product.attributeDisplay ? ` (${product.attributeDisplay})` : ''),
      price: product.price,
      stock: product.stockQuantity
    });
  });

  // Style header
  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFF3F4F6' }
  };
}

function populatePaymentTypes(sheet) {
  sheet.columns = [
    { header: 'Payment Type', key: 'type', width: 20 },
    { header: 'Description', key: 'description', width: 40 }
  ];

  sheet.addRow({ type: 'cash_on_delivery', description: 'Customer pays upon delivery' });
  sheet.addRow({ type: 'fully_paid', description: 'Payment already received' });

  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFF3F4F6' }
  };
}

function populateOrderStatuses(sheet) {
  sheet.columns = [
    { header: 'Status', key: 'status', width: 20 },
    { header: 'Description', key: 'description', width: 40 }
  ];

  sheet.addRow({ type: 'Pending', description: 'Order awaiting confirmation' });
  sheet.addRow({ type: 'Confirmed', description: 'Order confirmed and ready for processing' });

  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFF3F4F6' }
  };
}

function applyDataValidation(ordersSheet, columns, workbook, selectedFields, numberOfItems) {
  const maxRows = 1000; // Apply validation for first 1000 rows

  columns.forEach((col, index) => {
    const colLetter = String.fromCharCode(65 + index);

    // Payment Type dropdown
    if (col.key === 'paymentType') {
      ordersSheet.dataValidations.add(`${colLetter}2:${colLetter}${maxRows}`, {
        type: 'list',
        allowBlank: false,
        formulae: ['"cash_on_delivery,fully_paid"'],
        showErrorMessage: true,
        errorStyle: 'error',
        errorTitle: 'Invalid Payment Type',
        error: 'Please select a valid payment type from the dropdown.'
      });
    }

    // Order Status dropdown
    if (col.key === 'orderStatus') {
      ordersSheet.dataValidations.add(`${colLetter}2:${colLetter}${maxRows}`, {
        type: 'list',
        allowBlank: false,
        formulae: ['"pending,confirmed"'],
        showErrorMessage: true,
        errorStyle: 'error',
        errorTitle: 'Invalid Order Status',
        error: 'Please select a valid status from the dropdown.'
      });
    }

    // Phone number validation
    if (col.key === 'phone1' || col.key === 'phone2') {
      ordersSheet.dataValidations.add(`${colLetter}2:${colLetter}${maxRows}`, {
        type: 'textLength',
        operator: 'between',
        showErrorMessage: true,
        allowBlank: col.key === 'phone2',
        formulae: [10, 15],
        errorStyle: 'warning',
        errorTitle: 'Invalid Phone Number',
        error: 'Phone number must be 10-15 digits.'
      });
    }

    // Quantity validation
    if (col.key && col.key.includes('Quantity')) {
      ordersSheet.dataValidations.add(`${colLetter}2:${colLetter}${maxRows}`, {
        type: 'whole',
        operator: 'greaterThan',
        showErrorMessage: true,
        allowBlank: !col.required,
        formulae: [0],
        errorStyle: 'error',
        errorTitle: 'Invalid Quantity',
        error: 'Quantity must be a positive number greater than 0.'
      });
    }

    // Price validation (delivery charge, discount, custom line total)
    if (col.key === 'deliveryCharge' || col.key === 'totalDiscount' || 
        (col.key && col.key.includes('CustomLineTotal'))) {
      ordersSheet.dataValidations.add(`${colLetter}2:${colLetter}${maxRows}`, {
        type: 'decimal',
        operator: 'greaterThanOrEqual',
        showErrorMessage: true,
        allowBlank: true,
        formulae: [0],
        errorStyle: 'warning',
        errorTitle: 'Invalid Amount',
        error: 'Amount must be a positive number or zero.'
      });
    }
  });
}

function applyConditionalFormatting(ordersSheet, columns, selectedFields, numberOfItems) {
  const maxRows = 1000;

  columns.forEach((col, index) => {
    const colLetter = String.fromCharCode(65 + index);
    
    // City validation - red border if not in reference
    if (col.key === 'cityName') {
      ordersSheet.addConditionalFormatting({
        ref: `${colLetter}2:${colLetter}${maxRows}`,
        rules: [
          {
            type: 'expression',
            formulae: [`ISERROR(MATCH(${colLetter}2,'Cities Reference'!$A:$A,0))`],
            style: {
              fill: {
                type: 'pattern',
                pattern: 'solid',
                bgColor: { argb: INVALID_FIELDS_COLOR }
              },
              border: {
                top: { style: 'medium', color: { argb: INVALID_BORDER_COLOR } },
                bottom: { style: 'medium', color: { argb: INVALID_BORDER_COLOR } },
                left: { style: 'medium', color: { argb: INVALID_BORDER_COLOR } },
                right: { style: 'medium', color: { argb: INVALID_BORDER_COLOR } }
              }
            }
          }
        ]
      });
    }

    // SKU validation - red border if not in reference
    if (col.key && col.key.includes('Sku')) {
      ordersSheet.addConditionalFormatting({
        ref: `${colLetter}2:${colLetter}${maxRows}`,
        rules: [
          {
            type: 'expression',
            formulae: [`AND(LEN(${colLetter}2)>0,ISERROR(MATCH(${colLetter}2,'Products Reference'!$A:$A,0)))`],
            style: {
              fill: {
                type: 'pattern',
                pattern: 'solid',
                bgColor: { argb: INVALID_FIELDS_COLOR }
              },
              border: {
                top: { style: 'medium', color: { argb: INVALID_BORDER_COLOR } },
                bottom: { style: 'medium', color: { argb: INVALID_BORDER_COLOR } },
                left: { style: 'medium', color: { argb: INVALID_BORDER_COLOR } },
                right: { style: 'medium', color: { argb: INVALID_BORDER_COLOR } }
              }
            }
          }
        ]
      });
    }

    // Highlight required fields with yellow background
    if (col.required) {
      ordersSheet.addConditionalFormatting({
        ref: `${colLetter}2:${colLetter}${maxRows}`,
        rules: [
          {
            type: 'expression',
            formulae: ['TRUE'],
            style: {
              fill: {
                type: 'pattern',
                pattern: 'solid',
                bgColor: { argb: REQUIRED_FIELDS_COLOR }
              }
            }
          }
        ]
      });
    }

  });
}